import { Component, inject, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { HttpClient, HttpContext } from '@angular/common/http';
import { expand, EMPTY, forkJoin, map, reduce } from 'rxjs';
import { Bundle } from 'fhir/r4';
import { backendEndPointToken } from '../../app.config';
import { AuthService } from '../../shared/auth/auth.service';
import { UtilityService } from '../../shared/utility.service';
import { LoadingUIEnabled } from '../../loading.interceptor';

export interface PractitionerOption {
  id: string;
  name: string;
  roleDisplay: string;
}

@Component({
  selector: 'app-finalize-triage-dialog',
  standalone: true,
  imports: [
    CommonModule, 
    MatDialogModule, 
    MatButtonModule, 
    MatIconModule, 
    MatRadioModule, 
    MatSelectModule,
    MatProgressSpinnerModule,
    FormsModule,
    ReactiveFormsModule
  ],
  templateUrl: './finalize-triage-dialog.component.html',
  styleUrls: ['./finalize-triage-dialog.component.css']
})
export class FinalizeTriageDialogComponent implements OnInit {
  dialogRef = inject(MatDialogRef<FinalizeTriageDialogComponent>, { optional: true });
  @Output() onComplete = new EventEmitter<any>();
  @Output() onCancel = new EventEmitter<void>();
  @Input() isStepperMode = false;

  http = inject(HttpClient);
  backendUrl = inject(backendEndPointToken);
  authService = inject(AuthService);
  utilityService = inject(UtilityService);

  selectedLevel: number | null = null;
  showEmergencyConfirmation = false;
  
  practitioners: PractitionerOption[] = [];
  loadingPractitioners = false;
  selectedPractitionerId: string | null = null;

  triageLevels = [
    { level: 1, name: 'Level 1 - Resuscitation', code: 'STAT', display: 'stat', color: '#d32f2f' },
    { level: 2, name: 'Level 2 - Emergent', code: 'UR', display: 'urgent', color: '#ed6c02' },
    { level: 3, name: 'Level 3 - Urgent', code: 'UR', display: 'urgent', color: '#fbc02d' },
    { level: 4, name: 'Level 4 - Less Urgent', code: 'R', display: 'routine', color: '#2e7d32' },
    { level: 5, name: 'Level 5 - Non-urgent', code: 'R', display: 'routine', color: '#1976d2' }
  ];

  ngOnInit() {
    this.loadPractitioners();
  }

  private loadPractitioners() {
    this.loadingPractitioners = true;
    const ctx = new HttpContext().set(LoadingUIEnabled, false);
    
    const authService: any = this.authService;
    const user: any = authService.user?.getValue ? authService.user.getValue() : authService.user;
    const orgId = user?.organizationId;
    
    let url = `${this.backendUrl}/PractitionerRole?_include=PractitionerRole:practitioner&_count=200`;
    if (orgId) {
      url += `&organization=Organization/${orgId}`;
    }

    const todayStr = new Date().toISOString().split('T')[0];
    const startGe = encodeURIComponent(`${todayStr}T00:00:00Z`);
    const startLe = encodeURIComponent(`${todayStr}T23:59:59Z`);
    const slotsUrl = `${this.backendUrl}/Slot?start=ge${startGe}&start=le${startLe}&status=free&_include=Slot:schedule`;

    const fetchRoles = this.http.get<Bundle<any>>(url, { context: ctx }).pipe(
      expand((bundle, pageIndex) => {
        if (pageIndex + 1 >= 100) return EMPTY;
        const nextLink = (bundle?.link ?? []).find(link => String(link?.relation ?? '').toLowerCase() === 'next')?.url;
        const nextUrl = this.utilityService.normalizeNextLinkUrl(nextLink ?? null, url);
        return nextUrl ? this.http.get<Bundle<any>>(nextUrl, { context: ctx }) : EMPTY;
      }),
      map(bundle => bundle.entry || []),
      reduce((acc, curr) => acc.concat(curr), [] as any[])
    );

    const fetchSlots = this.http.get<Bundle<any>>(slotsUrl, { context: ctx }).pipe(
      expand((bundle, pageIndex) => {
        if (pageIndex + 1 >= 50) return EMPTY;
        const nextLink = (bundle?.link ?? []).find(link => String(link?.relation ?? '').toLowerCase() === 'next')?.url;
        const nextUrl = this.utilityService.normalizeNextLinkUrl(nextLink ?? null, slotsUrl);
        return nextUrl ? this.http.get<Bundle<any>>(nextUrl, { context: ctx }) : EMPTY;
      }),
      map(bundle => bundle.entry || []),
      reduce((acc, curr) => acc.concat(curr), [] as any[])
    );

    forkJoin({ rolesData: fetchRoles, slotsData: fetchSlots }).subscribe({
      next: ({ rolesData, slotsData }) => {
        const roles = rolesData.filter((e: any) => e.resource?.resourceType === 'PractitionerRole').map((e: any) => e.resource);
        const practitionersData = rolesData.filter((e: any) => e.resource?.resourceType === 'Practitioner').map((e: any) => e.resource);
        
        // Find which practitioners actually have a schedule today
        const schedules = slotsData.filter((e: any) => e.resource?.resourceType === 'Schedule').map((e: any) => e.resource);
        const practitionersWithSlots = new Set<string>();
        schedules.forEach((sch: any) => {
          const actorRef = sch.actor?.find((a: any) => a.reference?.startsWith('Practitioner/'))?.reference;
          if (actorRef) {
            practitionersWithSlots.add(actorRef.replace('Practitioner/', ''));
          }
        });
        
        const options: PractitionerOption[] = [];
        roles.forEach((role: any) => {
          if (role.practitioner?.reference) {
            const pId = role.practitioner.reference.replace('Practitioner/', '');
            
            // FILTER: Only add practitioner if they have slots today!
            if (!practitionersWithSlots.has(pId)) {
              return;
            }

            const p = practitionersData.find((x: any) => x.id === pId);
            if (p && p.name && p.name[0]) {
              const name = `${p.name[0].given?.join(' ')} ${p.name[0].family}`;
              const roleDisplay = role.code?.[0]?.coding?.[0]?.display || role.code?.[0]?.text || 'Practitioner';
              
              if (!options.some(opt => opt.id === pId)) {
                options.push({ id: pId, name, roleDisplay });
              }
            }
          }
        });
        
        this.practitioners = options.sort((a, b) => a.name.localeCompare(b.name));
      },
      error: (err) => {
        console.error('Failed to load practitioners', err);
        this.loadingPractitioners = false;
      },
      complete: () => {
        this.loadingPractitioners = false;
      }
    });
  }

  cancel() {
    this.onCancel.emit();
    if (!this.isStepperMode && this.dialogRef) {
      this.dialogRef.close(null);
    }
  }

  submitPhase1() {
    if (!this.selectedLevel) return;

    if (this.selectedLevel === 1 || this.selectedLevel === 2) {
      this.showEmergencyConfirmation = true;
    } else {
      this.submitFinal(false);
    }
  }
  
  cancelEmergency() {
    this.submitFinal(false);
  }
  
  confirmEmergency() {
    if (!this.selectedPractitionerId) return;
    this.submitFinal(true);
  }

  private submitFinal(createEmergency: boolean) {
    const levelData = this.triageLevels.find(l => l.level === this.selectedLevel);
    if (!levelData) return;

    const priorityConcept = {
      coding: [{
        system: 'http://terminology.hl7.org/CodeSystem/v3-ActPriority',
        code: levelData.code,
        display: levelData.display
      }],
      text: levelData.name
    };

    const result = { 
      priorityConcept, 
      createEmergency,
      practitionerId: createEmergency ? this.selectedPractitionerId : null
    };

    this.onComplete.emit(result);
    if (!this.isStepperMode && this.dialogRef) {
      this.dialogRef.close(result);
    }
  }
}
